import { useCallback } from 'react';
import { FileRejection } from 'react-dropzone';
import { FileContent } from 'components/common/types';
import { NotificationMessages } from 'consts/notifications';
import { UseFileUploadParams, UseFileUploadResult } from './useFileUpload.types';

export const DEFAULT_MAX_FILE_UPLOADS = 5;

export const useFileUpload = ({
  uploadedFiles,
  setUploadedFiles,
  existingFiles = [],
  maxFileUploads = DEFAULT_MAX_FILE_UPLOADS,
  maxFileRows,
  processFile,
  mapToFileContents,
  onNotification,
  abortOnRejectedFiles = false,
}: UseFileUploadParams): UseFileUploadResult => {
  const onFileChange = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]): Promise<void> => {
      if (rejectedFiles.length) {
        onNotification?.({ description: NotificationMessages.INVALID_UPLOAD, type: 'Failure' });
        if (abortOnRejectedFiles) return;
      }

      const existingNames = new Set([
        ...existingFiles.map((file) => file.name),
        ...uploadedFiles.map((file) => file.name),
      ]);
      const isDuplicateFile = acceptedFiles.some((accepted) => existingNames.has(accepted.name));

      if (isDuplicateFile) {
        onNotification?.({ description: NotificationMessages.DUPLICATE_UPLOAD, type: 'Failure' });
        return;
      }

      const results = await Promise.allSettled(acceptedFiles.map((file) => processFile(file)));

      const validFiles: File[] = [];
      const processedData: FileContent[][] = [];

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          validFiles.push(acceptedFiles[index]);
          processedData.push(result.value);
          return;
        }

        const message = result.reason instanceof Error ? result.reason.message : 'Error processing files';
        onNotification?.({ description: message, type: 'Failure' });
      });

      if (validFiles.length === 0) {
        return;
      }

      if (maxFileRows && processedData.some((data) => data.length > maxFileRows)) {
        onNotification?.({ description: `File upload cannot exceed ${maxFileRows} rows.`, type: 'Failure' });
        return;
      }

      const allUploadedFiles = [...uploadedFiles];
      validFiles.forEach((file) => {
        allUploadedFiles.push({ include: true, file, name: file.name });
      });

      mapToFileContents(processedData, allUploadedFiles, validFiles);
      setUploadedFiles(allUploadedFiles);
    },
    [
      uploadedFiles,
      setUploadedFiles,
      existingFiles,
      maxFileRows,
      processFile,
      mapToFileContents,
      onNotification,
      abortOnRejectedFiles,
    ],
  );

  return { onFileChange, hasReachedFileUploadLimit: uploadedFiles.length >= maxFileUploads };
};
